<template>
  <Popover as="div" class="relative inline-block text-left">
    <div>
      <PopoverButton
        class="group w-8 h-8 rounded-md border border-gray-700 bg-gray-800 duration-200 text-left text-sm font-medium text-text-primary hover:bg-gray-700 focus:outline-none focus:ring-0 flex items-center justify-center"
      >
        <CogIcon class="h-4 w-4 text-text-primary group-hover:text-text-primary" />
      </PopoverButton>
    </div>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <PopoverPanel
        v-if="state.navigateLength"
        class="absolute w-[240px] mt-2 right-0 origin-top rounded-md border border-gray-700 shadow-lg outline-none bg-gray-950 z-50 max-h-[420px] flex flex-col"
      >
        <div class="flex px-4 pt-4 pb-2 items-center gap-x-2 flex-shrink-0">
          <img src="../assets/icons/leftarrow.svg" @click="state.navigateLength = false" class="cursor-pointer hover:scale-110 duration-300" />
          <h2 class="uppercase text-text-secondary tracking-[1.5px] text-xs whitespace-nowrap">{{ $t('training.routine_settings.choose_length') }}</h2>
          <div class="w-full h-[1px] bg-gray-700" />
        </div>
        <div class="w-full flex flex-col pb-2 flex-1 overflow-y-auto">
          <div
            v-for="length in lengthOptions"
            :key="length.value"
            class="px-4 flex text-sm items-center py-3 cursor-pointer"
            :class="[length.value === selectedLength ? 'text-text-primary bg-gray-800' : ' bg-gray-950 text-text-secondary hover:bg-gray-900']"
            @click="updateLength(length.value)"
          >
            <h2 class="flex-1">{{ length.label }}</h2>
          </div>
        </div>
      </PopoverPanel>
      <PopoverPanel
        v-else
        class="absolute w-[240px] p-4 mt-2 right-0 origin-top rounded-md border border-gray-700 shadow-lg outline-none bg-gray-950 z-50"
      >
        <div class="flex gap-x-2 justify-center items-center pb-2">
          <h2 class="uppercase text-text-secondary tracking-[1.5px] text-xs">{{ $t('common.length') }}</h2>
          <div class="w-full h-[1px] bg-gray-700" />
        </div>

        <div class="flex items-center group cursor-pointer hover:scale-[102%] duration-300" @click="state.navigateLength = true">
          <h2 class="text-sm text-text-primary flex-1">{{ getCurrentLengthLabel() }}</h2>
          <img src="../assets/icons/rightarrow.svg" class="cursor-pointer" />
        </div>
        <div class="flex gap-x-2 justify-center items-center pb-2 pt-4">
          <h2 class="uppercase text-text-secondary tracking-[1.5px] text-xs">{{ $t('training.routine_settings.options') }}</h2>
          <div class="w-full h-[1px] bg-gray-700" />
        </div>
        <div class="flex items-center">
          <h2 class="text-sm text-text-primary flex-1">{{ $t('training.routine_settings.random_maps') }}</h2>
          <button
            @click="toggleRandomMaps"
            class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none"
            :class="selectedRandomMaps ? 'bg-primary-600' : 'bg-gray-700'"
          >
            <span
              class="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200"
              :class="selectedRandomMaps ? 'translate-x-[18px]' : 'translate-x-1'"
            />
          </button>
        </div>

        <!-- Created section -->
        <div class="flex gap-x-2 justify-center items-center pb-2 pt-4">
          <h2 class="uppercase text-text-secondary tracking-[1.5px] text-xs">{{ $t('training.routine_settings.created') }}</h2>
          <div class="w-full h-[1px] bg-gray-700" />
        </div>
        <div class="flex items-center group">
          <div class="flex flex-col gap-1 flex-1">
            <h2 class="text-sm text-text-primary">{{ getFormattedDate() }}</h2>
          </div>
          <button
            @click="$emit('refresh-routine')"
            :disabled="isRefreshing"
            class="w-8 h-8 rounded-md flex items-center justify-center duration-300 transition-colors"
            :class="{
              'bg-success-500 hover:bg-success-400': showSuccessFlash,
              'bg-gray-700 hover:bg-gray-600': !showSuccessFlash && !isRefreshing,
              'bg-gray-800': isRefreshing,
            }"
          >
            <svg
              v-if="showSuccessFlash"
              class="w-4 h-4 text-text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <svg
              v-else-if="!isRefreshing"
              class="w-4 h-4 text-text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <svg
              v-else
              class="w-4 h-4 animate-spin text-text-secondary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </button>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import {
  ref, reactive, computed, watch,
} from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { CogIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { t as globalT } from '../i18n';

const { t } = useI18n();

const props = defineProps({
  // Current selections
  currentLength: {
    type: [String, Number],
    default: '',
  },
  // Available options
  lengthOptions: {
    type: Array,
    default: () => [
      { value: '5', label: globalT('training.routine_settings.length_minutes', { n: 5 }) },
      { value: '10', label: globalT('training.routine_settings.length_minutes', { n: 10 }) },
      { value: '15', label: globalT('training.routine_settings.length_minutes', { n: 15 }) },
      { value: '30', label: globalT('training.routine_settings.length_minutes', { n: 30 }) },
      { value: '60', label: globalT('training.routine_settings.length_one_hour') },
    ],
  },
  // Refresh state
  isRefreshing: {
    type: Boolean,
    default: false,
  },
  // Success flash state
  showSuccessFlash: {
    type: Boolean,
    default: false,
  },
  // Updated date
  updatedAt: {
    type: String,
    default: '',
  },
  // Random maps toggle
  currentRandomMaps: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:length',
  'update:randomMaps',
  'refresh-routine',
]);

// Navigation state
const state = reactive({
  navigateLength: false,
});

// Local state for selections
const selectedLength = ref(props.currentLength);
const selectedRandomMaps = ref(props.currentRandomMaps);

// Watch for prop changes to update local state
watch(() => props.currentLength, (newVal) => {
  selectedLength.value = newVal;
});

watch(() => props.currentRandomMaps, (newVal) => {
  selectedRandomMaps.value = newVal;
});

// Helper functions to get current labels
function getCurrentLengthLabel() {
  const currentOption = props.lengthOptions.find((option) => option.value === selectedLength.value);
  return currentOption ? currentOption.label : t('training.routine_settings.select_length');
}

function getFormattedDate() {
  if (!props.updatedAt) return t('training.routine_settings.no_date');

  try {
    const date = new Date(props.updatedAt);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    return t('training.routine_settings.invalid_date');
  }
}

// Update functions
function updateLength(length) {
  selectedLength.value = length;
  state.navigateLength = false;
  emit('update:length', length);
}

function toggleRandomMaps() {
  selectedRandomMaps.value = !selectedRandomMaps.value;
  emit('update:randomMaps', selectedRandomMaps.value);
}
</script>
